import React, { FC, ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { Tooltip } from "@mui/material";
import { useMenuPlacement } from "./useMenuPlacement";
import { GlyphChevronDown, GlyphChevronUp } from "./glyphs";

export const MenuTooltip = ({ title, body }: { title: string; body: string }) => (
  <div style={{ padding: "2px 1px" }}>
    <div
      style={{
        fontWeight: 700,
        fontSize: 12.5,
        color: "var(--accent-text)",
        marginBottom: 3,
      }}
    >
      {title}
    </div>
    <div style={{ fontSize: 11.5, lineHeight: 1.4, color: "var(--text-2)" }}>{body}</div>
  </div>
);

export const STATUS_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      backgroundColor: "var(--surface)",
      color: "var(--text)",
      border: "1px solid var(--border)",
      borderRadius: "10px",
      boxShadow: "var(--sh-2)",
      padding: "8px 11px",
      maxWidth: 230,
    },
  },
  arrow: {
    sx: {
      color: "var(--surface)",
      "&::before": { border: "1px solid var(--border)" },
    },
  },
};

const PANEL_WIDTH = 352;

// "secondary": the same button, toned down (tint) so a primary action beside
// it (e.g. Review video) stands out.
export type MenuTriggerVariant = "primary" | "secondary" | "icon" | "card";

export interface MenuPopoverProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  variant?: MenuTriggerVariant;
  disabled?: boolean;
  label?: string;
  tooltip?: ReactNode;
  triggerTitle?: string;
  disabledTitle?: string;
  glyph: (props: { size?: number; strokeWidth?: number }) => ReactNode;
  glyphSize?: number;
  stopPropagation?: boolean;
  children: ReactNode;
}

export const MenuPopover: FC<MenuPopoverProps> = ({
  open,
  onToggle,
  onClose,
  variant = "primary",
  disabled = false,
  label = "Download",
  tooltip,
  triggerTitle = "Download",
  disabledTitle = "Not available for download",
  glyph,
  glyphSize = 18,
  stopPropagation = true,
  children,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const place = useMenuPlacement(open, triggerRef, panelRef, PANEL_WIDTH);

  /**
   * Click-away, hand-rolled — deliberately NOT MUI's `ClickAwayListener`.
   *
   * That component decides "was this click inside me?" from a flag it sets on
   * the child's click and clears only when its own document handler runs. The
   * wrapper here must call `stopPropagation()` so the button does not also open
   * the video card behind it — which prevented that handler from ever running,
   * left the flag stuck on, and made the FIRST outside click get swallowed
   * clearing it. That is the "click outside twice to close" bug.
   *
   * Containment is tested directly against both nodes instead, on the CAPTURE
   * phase so no handler in between can suppress it.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  const activate = () => {
    if (!disabled) onToggle();
  };

  const trigger = (): ReactNode => {
    if (variant === "card") {
      return (
        <button
          type="button"
          aria-label={disabled ? disabledTitle : triggerTitle}
          title={disabled ? disabledTitle : triggerTitle}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={activate}
          disabled={disabled}
          className={classNames(
            "inline-flex items-center justify-center w-[30px] h-[30px] rounded-lg border border-border-token bg-surface transition-colors",
            disabled
              ? "text-muted opacity-45 cursor-not-allowed"
              : "text-text-2 cursor-pointer hover:border-accent hover:text-text-1",
          )}
        >
          {glyph({ size: 16, strokeWidth: 1.8 })}
        </button>
      );
    }

    if (variant === "icon") {
      const button = (
        <button
          type="button"
          aria-label={typeof tooltip === "string" ? tooltip : triggerTitle}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={activate}
          disabled={disabled}
          className={classNames(
            "flex items-center justify-center border border-transparent p-1 rounded-md transition-colors text-muted",
            disabled
              ? "cursor-not-allowed opacity-40"
              : "cursor-pointer hover:bg-surface-2 hover:border-muted",
          )}
        >
          {glyph({ size: glyphSize, strokeWidth: 1.8 })}
        </button>
      );
      // MUI needs a non-disabled child to bind its listeners to.
      return tooltip ? (
        <Tooltip title={tooltip} arrow componentsProps={STATUS_TOOLTIP_PROPS}>
          <span className="inline-flex">{button}</span>
        </Tooltip>
      ) : (
        button
      );
    }

    const secondary = variant === "secondary";

    return (
      <button
        type="button"
        onClick={activate}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        title={disabled ? disabledTitle : triggerTitle}
        className={classNames(
          "rounded-xl border-0 min-w-[120px] h-[40px] transition-colors",
          disabled && "cursor-not-allowed bg-surface-2 text-muted",
          !disabled && secondary && "cursor-pointer bg-tint text-accent-text hover:bg-accent hover:text-white",
          !disabled && !secondary && "cursor-pointer bg-accent text-white hover:bg-accent-dark",
        )}
      >
        <span
          className="flex items-center justify-center gap-[6px] px-3"
          style={{ fontSize: 14, fontFamily: secondary ? "CircularStd" : "CircularStdBlack" }}
        >
          {label}
          {open ? <GlyphChevronUp size={13} strokeWidth={2.4} /> : <GlyphChevronDown size={13} strokeWidth={2.4} />}
        </span>
      </button>
    );
  };

  return (
    <>
      <div
        ref={triggerRef}
        className="relative"
        onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      >
        {trigger()}
      </div>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed border border-border-token rounded-xl bg-surface flex flex-col"
            style={{
              top: place.top,
              left: place.left,
              width: place.ready ? place.width : PANEL_WIDTH,
              maxHeight: place.ready ? place.maxHeight : undefined,
              overflowY: "auto",
              zIndex: 10000,
              boxShadow: "var(--sh-2)",
              // Painted only once measured, so it cannot flash at the wrong
              // place on the frame before its size is known.
              visibility: place.ready ? "visible" : "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
};
